/**
 * @file   DemandHistogram.js
 * @brief  Interactive demand histogram for Match tab showing Want-Listing price distribution.
 *         12 clickable bars - tap any bar to see its price range in a tooltip.
 *         "Your price" tick on the axis shows where the asking price falls.
 */
import React, { useMemo, useState } from "react";
import { formatCompactPrice, computeSweetSpotRange } from "../../../helpers/priceCapUtils";
import "./DemandHistogram/DemandHistogram.scss";

const NUM_BINS = 12;
const MIN_BAR_HEIGHT_PCT = 4;

/**
 * Compute fixed-scale histogram bins from price data.
 */
const computeBins = (prices, rangeMin, rangeMax) => {
  if (!prices || prices.length === 0 || !rangeMax || rangeMax <= 0) return null;
  const span = rangeMax - rangeMin;
  if (span <= 0) return null;

  const binWidth = span / NUM_BINS;
  const bins = Array.from({ length: NUM_BINS }, (_, i) => ({
    count: 0,
    start: rangeMin + i * binWidth,
    end: rangeMin + (i + 1) * binWidth,
  }));

  prices.forEach((price) => {
    let idx = Math.floor((price - rangeMin) / binWidth);
    if (idx >= NUM_BINS) idx = NUM_BINS - 1;
    if (idx < 0) idx = 0;
    bins[idx].count++;
  });

  return bins;
};

const DemandHistogram = ({
  prices,
  adType,
  askingPrice,
  activeBinIdx: controlledActiveBinIdx,
  onBinClick,
  rangeMin,
  rangeMax,
}) => {
  const [internalActiveBinIdx, setInternalActiveBinIdx] = useState(null);
  const isControlled = onBinClick != null;
  const activeBinIdx = isControlled ? controlledActiveBinIdx : internalActiveBinIdx;
  const handleBinClick = (i) => {
    if (isControlled) onBinClick(i);
    else setInternalActiveBinIdx((prev) => (prev === i ? null : i));
  };
  const bins = useMemo(() => computeBins(prices, rangeMin, rangeMax), [prices, rangeMin, rangeMax]);

  const sweetSpot = useMemo(
    () => computeSweetSpotRange(prices, adType),
    [prices, adType],
  );

  if (!bins) return null;

  const maxCount = Math.max(...bins.map((b) => b.count));
  if (maxCount === 0) return null;

  // Find peak bin index
  let peakIdx = 0;
  bins.forEach((b, i) => {
    if (b.count > bins[peakIdx].count) peakIdx = i;
  });

  // Calculate "Your price" tick position (as percentage of width)
  const span = rangeMax - rangeMin;
  const yourPricePct = askingPrice > 0 && span > 0
    ? Math.min(Math.max(((askingPrice - rangeMin) / span) * 100, 0), 100)
    : null;

  return (
    <div className="demand-histogram" data-tour="demand-histogram">
      {/* Header */}
      <div className="demand-histogram__header">
        <span className="demand-histogram__header-label">Buyer price ranges</span>
      </div>

      {/* Histogram bars */}
      <div className="demand-histogram__chart">
        {bins.map((bin, i) => {
          const heightPct = maxCount > 0
            ? Math.max((bin.count / maxCount) * 100, bin.count > 0 ? MIN_BAR_HEIGHT_PCT : 0)
            : 0;

          // Bar is in sweet spot zone
          const inSweetSpot = sweetSpot
            ? bin.end > sweetSpot.min && bin.start < sweetSpot.max
            : false;

          const isPeak = i === peakIdx;
          const isActive = i === activeBinIdx;

          let barClass = "demand-histogram__bar";
          if (isActive) barClass += " demand-histogram__bar--active";
          else if (inSweetSpot) barClass += " demand-histogram__bar--sweet";
          else if (bin.count > 0) barClass += " demand-histogram__bar--default";
          if (isPeak && !isActive) barClass += " demand-histogram__bar--peak";

          return (
            <div
              key={i}
              className={`demand-histogram__col ${isActive ? "demand-histogram__col--active" : ""}`}
              onClick={() => handleBinClick(i)}
            >
              <div
                className={barClass}
                style={{ height: `${heightPct}%` }}
              />
              {/* Tooltip above active bar */}
              {isActive && (
                <div className="demand-histogram__bin-tooltip">
                  {formatCompactPrice(bin.start)} - {formatCompactPrice(bin.end)}
                  <span className="demand-histogram__bin-tooltip-count">
                    {bin.count} buyer{bin.count !== 1 ? "s" : ""}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Axis with price range labels and "Your price" tick */}
      <div className="demand-histogram__axis">
        <span>{formatCompactPrice(rangeMin || 0)}</span>
        {yourPricePct != null && (
          <div
            className="demand-histogram__your-price-tick"
            style={{ left: `clamp(12%, ${yourPricePct}%, 88%)` }}
          >
            <div className="demand-histogram__your-price-arrow" />
            <span className="demand-histogram__your-price-label">Your price</span>
          </div>
        )}
        <span>{formatCompactPrice(rangeMax)}</span>
      </div>
    </div>
  );
};

export default DemandHistogram;
